import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../utility/axiosInstance";

export interface ClickhouseDatabase {
    id: number;
    name: string;
    engine: "clickhouse";
    db_name: string;
    db_user: string;
    db_version: string;
    status: "provisioning" | "ready" | "error";
    floating_ip: string | null;
    port: number;
    instance_id: string;
    created_at: string;
    access_point?: string | null;
    private_ip?: string | null;
    // High availability. Optional because a row created before HA shipped, or an API build that
    // does not select these columns yet, simply omits them - and "absent" is not the same as
    // "false", so the UI must be able to tell the two apart rather than assuming single-machine.
    // `vip` is the address the customer connects to on an HA database: a member's own IP is wrong
    // the moment that member is rebuilt. `member_ips` is however the API serialises the member
    // list (a JSON array or a comma-separated string), so parse it defensively.
    ha?: boolean;
    vip?: string | null;
    member_ips?: string | string[] | null;
    // An upgrade-in-place is running RIGHT NOW. Derived server-side and bounded by the workflow's
    // own run timeout, so it cannot stick after a dead worker. Never recomputed here: a second
    // copy of "is it still going?" would eventually disagree with the API's copy, and the
    // disagreement would show as the upgrade button being offered mid-upgrade.
    ha_upgrade_in_progress?: boolean;
    /** The machine size, so the upgrade modal can price the change for real. */
    flavor_id?: string | null;
    // shown once on creation only
    db_password?: string;
}

interface ClickhouseDatabasesState {
    databases: ClickhouseDatabase[];
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

const initialState: ClickhouseDatabasesState = { databases: [], status: "idle", error: null };

export const fetchClickhouseDatabases = createAsyncThunk("clickhouseDatabases/fetch", async () => {
    const res = await axiosInstance.get("/v1/clickhouse");
    return res.data as ClickhouseDatabase[];
});

export const deleteClickhouseDatabase = createAsyncThunk("clickhouseDatabases/delete", async (id: number) => {
    await axiosInstance.delete(`/v1/clickhouse/${id}`);
    return id;
});

const clickhouseDatabasesSlice = createSlice({
    name: "clickhouseDatabases",
    initialState,
    reducers: {
        addClickhouseDatabase: (state, action) => { state.databases.unshift(action.payload); },
        updateClickhouseDatabaseStatus: (state, action) => {
            const db = state.databases.find(d => d.id === action.payload.id);
            if (db) { db.status = action.payload.status; db.floating_ip = action.payload.floating_ip ?? db.floating_ip; }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClickhouseDatabases.pending,   (s) => { s.status = "loading"; })
            .addCase(fetchClickhouseDatabases.fulfilled, (s, a) => { s.status = "succeeded"; s.databases = a.payload; })
            .addCase(fetchClickhouseDatabases.rejected,  (s, a) => { s.status = "failed"; s.error = a.error.message ?? null; })
            .addCase(deleteClickhouseDatabase.fulfilled, (s, a) => { s.databases = s.databases.filter(d => d.id !== a.payload); });
    },
});

export const { addClickhouseDatabase, updateClickhouseDatabaseStatus } = clickhouseDatabasesSlice.actions;
export default clickhouseDatabasesSlice.reducer;
